
import { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Textarea } from "../../components/ui/textarea"
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar"
import { Alert, AlertDescription } from "../../components/ui/alert"
import { Skeleton } from "../../components/ui/skeleton"
import { User, Edit, Save, X, CheckCircle, AlertCircle, Upload } from "lucide-react"

const API_URL = process.env.REACT_APP_API_URL
const API_ORIGIN = (() => {
  try {
    return new URL(API_URL).origin
  } catch (error) {
    return ""
  }
})()

export default function ProfilePage() {
  const [user, setUser] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false)
  const [message, setMessage] = useState({ type: "", text: "" })
  const [, setError] = useState("")
  const [authVerified, setAuthVerified] = useState(false)
  const confirmInputRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (isEditing && !authVerified && confirmInputRef.current) {
      try {
        confirmInputRef.current.focus()
      } catch (e) {
        // ignore
      }
    }
  }, [isEditing, authVerified])

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    bio: "",
    location: "",
    phone: "",
    currentPassword: "",
  })

  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState("")
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef(null)

  const fetchUserProfile = useCallback(async () => {
    try {
      setIsLoading(true)
      setError("")
      const token = localStorage.getItem("token")

      if (!token) {
        setError("Please login to view your profile")
        navigate("/login")
        return
      }

      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      })

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("token")
          navigate("/login")
          return
        }
        throw new Error("Failed to fetch profile")
      }

      const data = await response.json()
      if (!data.success) {
        throw new Error(data.message || "Failed to fetch profile")
      }

      const userData = data.data
      setUser(userData)
      setFormData({
        name: userData.name || "",
        email: userData.email || "",
        bio: userData.bio || "",
        location: userData.location || "",
        phone: userData.phone || "",
        profileImage: userData.profileImage || "",
        currentPassword: "",
      })
    } catch (error) {
      console.error("Error fetching profile:", error)
      setError(error.message || "Failed to load profile")
      setMessage({ type: "error", text: error.message || "Failed to load profile" })
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    fetchUserProfile()
  }, [fetchUserProfile])

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const getProfileImageSrc = (profileImage) => {
    if (!profileImage) return "/placeholder.svg?height=80&width=80"
    if (profileImage.startsWith?.("http") || profileImage.startsWith?.("data:") || profileImage.startsWith?.("blob:")) {
      return profileImage
    }

    const cleanPath = profileImage.replace(/^\/+/, "")
    if (cleanPath.startsWith("uploads/")) {
      return `${API_ORIGIN}/${cleanPath}`
    }

    return `${API_ORIGIN}/uploads/${cleanPath}`
  }

  const getErrorMessage = async (response, fallback) => {
    try {
      const data = await response.json()
      return data.message || data.error || fallback
    } catch (error) {
      return fallback
    }
  }

  const handleConfirmPassword = async () => {
    if (!formData.currentPassword) {
      setMessage({ type: "error", text: "Please enter your current password to proceed" })
      return
    }

    try {
      setIsVerifyingPassword(true)
      const token = localStorage.getItem("token")

      if (!token) {
        navigate("/login")
        return
      }

      const response = await fetch(`${API_URL}/auth/verify-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword: formData.currentPassword }),
      })

      if (!response.ok) {
        const errorMessage = await getErrorMessage(response, "Password is incorrect")
        setAuthVerified(false)
        setMessage({ type: "error", text: errorMessage })
        return
      }

      setAuthVerified(true)
      setMessage({ type: "", text: "" })
    } catch (error) {
      console.error("Error verifying password:", error)
      setAuthVerified(false)
      setMessage({ type: "error", text: "Unable to verify password. Please try again." })
    } finally {
      setIsVerifyingPassword(false)
    }
  }

  const uploadProfileImage = (file, token) => {
    const uploadData = new FormData()
    uploadData.append("file", file)
    uploadData.append("currentPassword", formData.currentPassword)

    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest()
      request.open("PUT", `${API_URL}/auth/uploadprofile`)
      request.setRequestHeader("Authorization", `Bearer ${token}`)

      request.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setUploadProgress(Math.round((event.loaded / event.total) * 100))
        }
      }

      request.onload = () => {
        let data = {}
        try {
          data = JSON.parse(request.responseText || "{}")
        } catch (error) {
          data = {}
        }

        if (request.status >= 200 && request.status < 300 && data.success) {
          resolve(data.data)
          return
        }

        reject(new Error(data.message || data.error || "Image upload failed"))
      }

      request.onerror = () => reject(new Error("Image upload failed"))
      request.send(uploadData)
    })
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      const token = localStorage.getItem("token")

      if (!authVerified) {
        setMessage({ type: "error", text: "Please confirm your password before editing your profile" })
        setIsSaving(false)
        return
      }

      // If user changed email, require current password
      if (formData.email !== user?.email && !formData.currentPassword) {
        setMessage({ type: "error", text: "Current password is required to change email" })
        setIsSaving(false)
        return
      }

      if (selectedFile) {
        try {
          await uploadProfileImage(selectedFile, token)
        } catch (err) {
          console.error('Upload failed', err)
          setMessage({ type: 'error', text: err.message || 'Image upload failed' })
          setIsSaving(false)
          return
        }
      }

      const payload = { ...formData }

      const response = await fetch(`${API_URL}/auth/updatedetails`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        const data = await response.json()
        setUser(data.data)
        localStorage.setItem("user", JSON.stringify(data.data))
        window.dispatchEvent(new Event("hannacode:user-updated"))
        // ensure formData/profile state reflects saved image URL
        setFormData((prev) => ({ ...prev, profileImage: data.data.profileImage || prev.profileImage, currentPassword: "" }))
        setSelectedFile(null)
        setPreviewUrl("")
        setUploadProgress(0)
        setIsEditing(false)
        setMessage({ type: "success", text: "Profile updated successfully!" })

        // Clear message after 3 seconds
        setTimeout(() => setMessage({ type: "", text: "" }), 3000)
      } else {
        const errorMessage = await getErrorMessage(response, "Failed to update profile")
        throw new Error(errorMessage)
      }
    } catch (error) {
      console.error("Error updating profile:", error)
      setMessage({ type: "error", text: error.message || "Failed to update profile" })
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    // Reset form data to original user data
    setFormData({
      name: user?.name || "",
      email: user?.email || "",
      bio: user?.bio || "",
      location: user?.location || "",
      phone: user?.phone || "",
      currentPassword: "",
    })
    setAuthVerified(false)
    setSelectedFile(null)
    setPreviewUrl("")
    setMessage({ type: "", text: "" })
  }

  if (isLoading) {
    return (
      <div className="container py-8">
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-48" />
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center space-x-4">
              <Skeleton className="h-20 w-20 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-48" />
              </div>
            </div>
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container py-8">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <User className="mr-2 h-5 w-5" />
              My Profile
            </div>
            {!isEditing && (
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditing(true)
                  setAuthVerified(false)
                  setFormData((prev) => ({ ...prev, currentPassword: "" }))
                }}
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit Profile
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {message.text && (
            <Alert className={message.type === "success" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
              {message.type === "success" ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600" />
              )}
              <AlertDescription className={message.type === "success" ? "text-green-800" : "text-red-800"}>
                {message.text}
              </AlertDescription>
            </Alert>
          )}

          {/* Avatar Section */}
          {/* Password confirmation step shown when entering edit mode */}
          {isEditing && !authVerified && (
            <div className="mb-4 p-4 border rounded bg-muted/50">
              <div className="mb-2 font-medium">Confirm your password to edit profile</div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <Input
                  id="confirmPassword"
                  ref={confirmInputRef}
                  type="password"
                  value={formData.currentPassword}
                  onChange={(e) => handleInputChange("currentPassword", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleConfirmPassword()
                    }
                  }}
                  placeholder="Enter your current password"
                  className="flex-1"
                  aria-label="Confirm current password to edit profile"
                />
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button
                    type="button"
                    className="w-full sm:w-auto"
                    onClick={handleConfirmPassword}
                    disabled={!formData.currentPassword || isVerifyingPassword}
                  >
                    {isVerifyingPassword ? "Checking..." : "Confirm"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => {
                      // cancel edit
                      setIsEditing(false)
                      setAuthVerified(false)
                      setFormData({
                        name: user?.name || "",
                        email: user?.email || "",
                        bio: user?.bio || "",
                        location: user?.location || "",
                        phone: user?.phone || "",
                        currentPassword: "",
                      })
                      setSelectedFile(null)
                      setPreviewUrl("")
                      setMessage({ type: "", text: "" })
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center space-x-4">
            <Avatar className="h-20 w-20">
              <AvatarImage
                src={
                  previewUrl
                    ? previewUrl
                    : getProfileImageSrc(user?.profileImage)
                }
                alt="Profile"
              />
              <AvatarFallback className="text-lg">{user?.name?.charAt(0)?.toUpperCase() || "U"}</AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-semibold">{user?.name || "User"}</h3>
              <p className="text-muted-foreground">{user?.email}</p>
              {isEditing && authVerified && (
                <div className="mt-2">
                  <div className="inline-flex items-center">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0]
                        if (file) {
                          setSelectedFile(file)
                          setPreviewUrl(URL.createObjectURL(file))
                        }
                      }}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Change Photo
                    </Button>
                  </div>
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="text-sm text-muted-foreground mt-1">Uploading: {uploadProgress}%</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Profile Form */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                {isEditing ? (
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Enter your full name"
                    disabled={!authVerified}
                  />
                ) : (
                  <div className="p-2 bg-muted rounded-md">{user?.name || "Not provided"}</div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                {isEditing ? (
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="Enter your email"
                    disabled={!authVerified}
                  />
                ) : (
                  <div className="p-2 bg-muted rounded-md">{user?.email || "Not provided"}</div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                {isEditing ? (
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleInputChange("location", e.target.value)}
                    placeholder="Enter your location"
                    disabled={!authVerified}
                  />
                ) : (
                  <div className="p-2 bg-muted rounded-md">{user?.location || formData.location || "Not provided"}</div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                {isEditing ? (
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="Enter your phone number"
                    disabled={!authVerified}
                  />
                ) : (
                  <div className="p-2 bg-muted rounded-md">{user?.phone || formData.phone || "Not provided"}</div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              {isEditing ? (
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => handleInputChange("bio", e.target.value)}
                  placeholder="Tell us about yourself..."
                  rows={4}
                  disabled={!authVerified}
                />
              ) : (
                <div className="p-2 bg-muted rounded-md min-h-[100px]">
                  {user?.bio || formData.bio || "No bio provided"}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="flex gap-4 pt-4">
              <Button onClick={handleSave} disabled={isSaving || (isEditing && !authVerified)}>
                {isSaving ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
            </div>
          )}

          {/* Account Info */}
          <div className="pt-6 border-t">
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Account Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Member since:</span>
                <div>{user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : "Unknown"}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Account status:</span>
                <div className="text-green-600">Active</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
